import { merge } from "lodash-es";
import Vue from "vue";
import VueI18n from "vue-i18n";
import { getLocale, loadLocaleLocations, loadLocaleMessages } from "@/lib/localeHelper";
import config from "@/config";

Vue.use(VueI18n);

function ruPluralizationRule(choice: number): number {
  if (choice === -1) return 3;
  const mod10 = choice % 10;
  const mod100 = choice % 100;
  if (mod100 >= 11 && mod100 <= 19) return 2;
  if (mod10 === 1) return 0;
  if (mod10 >= 2 && mod10 <= 4) return 1;
  return 2;
}

const FALLBACK_LOCALE = process.env.VUE_APP_I18N_FALLBACK_LOCALE || "en";

const i18n = new VueI18n({
  locale: getLocale(),
  fallbackLocale: FALLBACK_LOCALE,
  silentTranslationWarn: true,
  formatFallbackMessages: true,
  pluralizationRules: {
    ru: ruPluralizationRule,
  },
});

const loadedLocales = new Map<string, Promise<void>>();
const loadedLocations = new Map<string, Promise<void>>();
let messagesLoaded = false;
let locationsLoaded = false;

// cached before it settles so concurrent callers share one load; failures are dropped so a retry can re-run
function loadOnce(cache: Map<string, Promise<void>>, locale: string, load: () => Promise<void>) {
  let loading = cache.get(locale);
  if (!loading) {
    loading = load();
    loading.catch(() => cache.delete(locale));
    cache.set(locale, loading);
  }
  return loading;
}

function ensureLocaleMessages(locale: string) {
  return loadOnce(loadedLocales, locale, async () => {
    const overrides = (config.localeOverrides as Record<string, VueI18n.LocaleMessageObject>)[locale];
    i18n.setLocaleMessage(locale, merge(await loadLocaleMessages(locale), overrides));
    messagesLoaded = true;
  });
}

function ensureLocaleLocations(locale: string) {
  return loadOnce(loadedLocations, locale, async () => {
    const locations = await loadLocaleLocations(locale);
    if (locations) i18n.mergeLocaleMessage(locale, { $locations: locations });
    locationsLoaded = true;
  });
}

async function loadLocaleOrFallback(locale: string) {
  try {
    await ensureLocaleMessages(locale);
    return locale;
  } catch (e) {
    console.error(`Locale "${locale}" is not available`, e);
    return FALLBACK_LOCALE;
  }
}

export function hasLoadedMessages() {
  return messagesLoaded;
}

export function hasLoadedLocations() {
  return locationsLoaded;
}

// the fallback is loaded too: locales without locations of their own fall back to it
export function ensureLocations(locale = i18n.locale) {
  return Promise.all([ensureLocaleLocations(locale), ensureLocaleLocations(FALLBACK_LOCALE)]);
}

export async function setLocale(locale: string) {
  // the fallback is needed either way, so it loads next to the requested locale instead of ahead of it
  const [resolved] = await Promise.all([loadLocaleOrFallback(locale), ensureLocaleMessages(FALLBACK_LOCALE)]);
  i18n.locale = resolved;
}

export default i18n;
